<script setup lang="ts">
import { Xmark } from "@iconoir/vue";
import { useMediaQuery } from "@vueuse/core";
import {
  DrawerClose,
  DrawerContent,
  DrawerHandle,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from "reka-ui";
import { computed, ref, watch } from "vue";

const emit = defineEmits<{
  close: [];
}>();

const open = defineModel<boolean>("open", { default: true });

watch(open, (isOpen) => {
  if (!isOpen) emit("close");
});

//Desktop
const isDesktop = useMediaQuery("(min-width: 40rem)");
const swipeDirection = computed(() => (isDesktop.value ? "right" : "down"));

const activeSnapPoint = ref<number | string | null>(0.33);
const expand: () => void = () => {
  activeSnapPoint.value = 1;
};
const isScrollable = computed(
  () => isDesktop.value || activeSnapPoint.value === 1,
);
</script>

<template>
  <DrawerRoot
    v-model:open="open"
    v-model:snap-point="activeSnapPoint"
    :modal="false"
    :swipe-direction="swipeDirection"
    :snap-points="isDesktop ? undefined : [0.33, 1]"
    :default-snap-point="isDesktop ? undefined : 0.33"
  >
    <DrawerPortal>
      <DrawerContent class="bottom-sheet" :class="{ 'is-desktop': isDesktop }">
        <DrawerHandle v-if="!isDesktop" class="bottom-sheet-handle" />
        <header class="bottom-sheet-header" @click="expand">
          <div class="bottom-sheet-heading">
            <DrawerTitle class="bottom-sheet-title row">
              <slot name="title"></slot>
            </DrawerTitle>
            <DrawerClose class="button button--tertiary">
              <Xmark />
              <span class="visually-hidden">Schließen</span>
            </DrawerClose>
          </div>
          <slot name="subheading"></slot>
        </header>
        <div
          class="bottom-sheet-body"
          :class="{ 'is-scrollable': isScrollable }"
        >
          <slot></slot>
        </div>
      </DrawerContent>
    </DrawerPortal>
  </DrawerRoot>
</template>

<style scoped>
.bottom-sheet {
  /* Area below the viewport edge, so the sheet stretches instead of
     detaching while it is pulled past its snap points. */
  --bleed: 3rem;

  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  block-size: 92dvh;
  max-inline-size: 40rem;
  margin-inline: auto;
  margin-block-end: calc(-1 * var(--bleed));
  padding-block-end: calc(env(safe-area-inset-bottom, 0px) + var(--bleed));
  background-color: var(--bg-color-main);
  border-start-start-radius: var(--border-radius-lg);
  border-start-end-radius: var(--border-radius-lg);
  box-shadow: 0 -4px 16px color-mix(in oklch, var(--grey-950) 15%, transparent);
  outline: none;
  transform: translateY(
    calc(
      var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px)
    )
  );
  transition: transform 300ms cubic-bezier(0.32, 0.72, 0, 1);
  will-change: transform;

  &[data-state="open"] {
    animation: slide-in 300ms cubic-bezier(0.32, 0.72, 0, 1);
  }

  &[data-state="closed"] {
    animation: slide-out 300ms cubic-bezier(0.32, 0.72, 0, 1);
  }

  &[data-swiping] {
    transition-duration: 0ms;
    user-select: none;
  }

  /* Side panel on wider screens, anchored to the inline-end edge */
  &.is-desktop {
    --bleed: 0px;

    inset-block: 0;
    inset-inline: auto 0;
    block-size: 100dvh;
    inline-size: 24rem;
    max-inline-size: 100%;
    margin: 0;
    padding-block-end: env(safe-area-inset-bottom, 0px);
    border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
    box-shadow: -4px 0 16px
      color-mix(in oklch, var(--grey-950) 15%, transparent);
    transform: translateX(var(--drawer-swipe-movement-x, 0px));

    &[data-state="open"] {
      animation: slide-in-side 300ms cubic-bezier(0.32, 0.72, 0, 1);
    }

    &[data-state="closed"] {
      animation: slide-out-side 300ms cubic-bezier(0.32, 0.72, 0, 1);
    }
  }
}

.bottom-sheet-handle {
  flex-shrink: 0;
  inline-size: 3rem;
  block-size: 0.375rem;
  margin: var(--spacing-sm) auto 0;
  border-radius: 9999px;
  background-color: var(--text-color-muted);
}

.bottom-sheet-header {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.bottom-sheet-heading {
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: var(--spacing-md) var(--spacing-sm);
  padding-bottom: 0px;
}

.bottom-sheet-body {
  flex: 1;
  padding: var(--spacing-sm);
  padding-top: 0px;

  &.is-scrollable {
    overflow-y: auto;
  }
}

/* The bleed is already below the viewport edge, so the sheet only travels
   `height - bleed` to clear it. */
@keyframes slide-in {
  from {
    translate: 0 calc(100% - var(--bleed));
  }
}

@keyframes slide-out {
  to {
    translate: 0 calc(100% - var(--bleed));
  }
}

@keyframes slide-in-side {
  from {
    translate: 100% 0;
  }
}

@keyframes slide-out-side {
  to {
    translate: 100% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bottom-sheet {
    animation: none;
    transition: none;
  }
}
</style>
